import { USER_TYPES } from '@medflex/utils/constants';
import Logger from '@medflex/utils/Logger';
import getUserType from '@medflex/utils/getUserType';
import env from '@medflex/utils/config';
import { RequestTypes, UserTypes } from '@medflex/shared-types';
import makeApiRequest from '../../../api/makeApiRequest';
import getFullNameWithTitle from '../../../utils/getFullNameWithTitle';

export const NAME = 'superuser';

const errors = {
  UNVERIFIED_EMAIL: 'UNVERIFIED_EMAIL',
};

const DEBUG = env.DEBUG === '1';
let G = () => null;
let GE = () => null;
let L = () => null;
let LN = () => null;

if (DEBUG) {
  const debugName = '[userProfile]';
  G = name => Logger.gc(`${debugName} ${name}`);
  GE = name => Logger.ge(`${debugName} ${name}`);
  LN = (...rest) => Logger.log(debugName, ...rest);
  L = Logger.log;
}

export const PAGES_WIDGET_SETTING_LABELS = {
  APPOINTMENT: RequestTypes.APPOINTMENT,
  GENERAL_INQUIRY: RequestTypes.GENERAL_INQUIRY,
  FINDINGS_INQUIRY: RequestTypes.FINDINGS_INQUIRY,
  REFERRAL: RequestTypes.REFERRAL,
  REQUEST_FOLLOW_UP_PRESCRIPTION: RequestTypes.REQUEST_FOLLOW_UP_PRESCRIPTION,
};

function normalizeUserProfile({ id, type, attributes }) {
  const { flags, ...attrs } = attributes;
  return {
    id,
    type: getUserType(type),
    attributes: {
      ...attrs,
      email: attrs.email,
      fullName: `${attrs.firstName} ${attrs.lastName}`,
      initials: `${attrs.firstName[0]}${attrs.lastName[0]}`,
      fullNameWithTitle: `${attrs.title || ''} ${attrs.firstName} ${attrs.lastName}`.trim(),
    },
    flags: {
      ...flags,
      isPhysician: type === 'physicians',
      isPatient: type === 'patients',
    },
  };
}

const userProfile = {
  name: NAME,
  state: null,
  reducers: {
    setUserProfile(state, payload) {
      if (DEBUG) G('-> setUserProfile');
      if (DEBUG) L('payload:', payload);
      if (DEBUG) GE('-> setUserProfile');

      // omit subscription, is handled by subscription model
      const { subscription, ...attributes } = payload.attributes;
      return {
        ...payload,
        attributes,
      };
    },
    onFlagsUpdated(state, payload) {
      const { flags, ...restOfState } = state || {};
      return { ...restOfState, flags: { ...flags, ...payload } };
    },
    onUpdated(state, payload) {
      return { ...state, attributes: { ...state.attributes, ...payload } };
    },
    onTagCreated(state, tag) {
      const { relationships: createdTagGroup, attributes: createdTag } = tag;

      return {
        ...state,
        attributes: {
          ...state.attributes,
          tags: state.attributes.tags.concat(createdTag),
          ...(createdTagGroup?.data && {
            tagGroups: state.attributes.tagGroups.concat(createdTagGroup.data),
          }),
        },
      };
    },
    onTagUpdated(state, tag) {
      const { relationships: createdTagGroup, attributes: updatedTag } = tag;

      return {
        ...state,
        attributes: {
          ...state.attributes,
          tags: state.attributes.tags.reduce((prev, tag) => {
            if (tag.id === updatedTag.id) {
              prev.push(updatedTag);
            } else {
              prev.push(tag);
            }
            return prev;
          }, []),
          ...(createdTagGroup?.data && {
            tagGroups: state.attributes.tagGroups.concat(createdTagGroup.data),
          }),
        },
      };
    },
    onTagDeleted(state, tagId) {
      const requestTagIds = state.attributes.pages.widgetSettings.requestTagIds.filter(
        id => id !== tagId,
      );
      return {
        ...state,
        attributes: {
          ...state.attributes,
          tags: state.attributes.tags.filter(tag => tagId !== tag.id),
          pages: {
            ...state.attributes.pages,
            widgetSettings: {
              ...state.attributes.pages.widgetSettings,
              requestTagIds,
            },
          },
        },
      };
    },
    '_chat/onMessage': (state, event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'PAGES_MODIFIED') {
        const { payload } = data;
        return {
          ...state,
          attributes: { ...state.attributes, pages: payload },
        };
      }
      return state;
    },
  },
  messageListeners: {
    USER_TAG_CREATED({ payload: tag }) {
      this.onTagCreated(tag);
    },
    USER_TAG_UPDATED({ payload: tag }) {
      this.onTagUpdated(tag);
    },
    USER_TAG_DELETED({ payload: tagIds }) {
      this.onTagDeleted(tagIds);
    },
  },
  effects: dispatch => ({
    async createUserProfile({ attributes }, { oidcSession }) {
      if (DEBUG) LN('createUserProfile');
      const config = {
        url: 'users',
        method: 'post',
        data: {
          data: {
            type: `${oidcSession.userType}${oidcSession.userType === USER_TYPES.STAFF ? '' : 's'}`,
            id: oidcSession.profile.userId,
            attributes,
          },
        },
        headers: {
          Authorization: `${oidcSession.token_type} ${oidcSession.access_token}`,
        },
      };
      const [request] = makeApiRequest(config);
      const { error } = await request;
      if (error) throw error;
      return dispatch.superuser.loadUserProfile();
    },
    async loadUserProfile(payload, { oidcSession }) {
      if (DEBUG) LN('loadUserProfile');
      const [request] = makeApiRequest(`users/${oidcSession.profile.userId}`, oidcSession);
      const { error, data, status } = await request;
      if (error) {
        if (DEBUG) LN('loadUserProfile -> Error', { error });

        if (status === 401) {
          if (data && data.errors && data.errors.some(e => e.detail === errors.UNVERIFIED_EMAIL)) {
            return dispatch.superuser.onFlagsUpdated({ unverified: true });
          }
        }

        if (status === 403) {
          return dispatch.superuser.onFlagsUpdated({ isNonExistent: true });
        }

        if (status === 404) {
          return dispatch.superuser.onFlagsUpdated({ isNotRegistered: true });
        }

        dispatch.errors.add({
          realm: 'userProfile',
          type: 'loadProfile',
          error,
        });

        throw error;
      }

      dispatch.errors.remove({
        realm: 'userProfile',
        type: 'loadProfile',
      });

      const normalizedUserProfile = normalizeUserProfile(data.data);
      return dispatch.superuser.setUserProfile(normalizedUserProfile);
    },
    async savePhysicianStatus({ status }, { oidcSession }) {
      const config = {
        url: '/user-status',
        method: 'put',
        data: { data: { attributes: { status } } },
      };
      /** @type any */
      const [request] = makeApiRequest(config, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({
          realm: 'physicianStatus',
          type: 'saveStatus',
          error,
        });
        return false;
      }
      this.onUpdated({
        status: data.data.attributes.status,
        statusSetAt: data.data.attributes.statusSetAt,
      });
      return true;
    },
    async saveGreetingMessage({ greetingMessage }, { oidcSession }) {
      const config = {
        url: `/physicians/${oidcSession.profile.userId}/greeting-message`,
        method: 'patch',
        data: { data: { attributes: { greetingMessage } } },
      };
      const [request] = makeApiRequest(config, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({
          realm: 'greetingMessage',
          type: 'saveGreetingMessage',
          error,
        });
        return false;
      }
      this.onUpdated({ greetingMessage });
      return true;
    },
    async saveAutoResponseMessage({ autoResponseMessage }, { oidcSession }) {
      const config = {
        url: `/physicians/${oidcSession.profile.userId}/auto-response-message`,
        method: 'patch',
        data: { data: { attributes: { autoResponseMessage: autoResponseMessage.trim() } } },
      };
      const [request] = makeApiRequest(config, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({
          realm: 'autoResponseMessage',
          type: 'saveAutoResponseMessage',
          error,
        });
        return false;
      }
      this.onUpdated({ autoResponseMessage });
      return true;
    },
    async updateWorkplace(workplace, { oidcSession }) {
      const config = {
        url: `users/${oidcSession.profile.userId}`,
        method: 'patch',
        data: {
          data: {
            attributes: { workplace },
            type: USER_TYPES.PHYSICIAN,
            id: oidcSession.profile.userId,
          },
        },
      };
      /** @type any */
      const [request] = makeApiRequest(config, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({
          realm: 'updateWorkplace',
          type: 'workplace',
          error,
        });
        throw error;
      }
      this.onUpdated({ workplace });
    },
    async update(data, { oidcSession }) {
      const attributes = Object.entries(data).reduce(
        (memo, [key, value]) => ({
          ...memo,
          [key]: value || null,
        }),
        {},
      );

      const dataProfile = {
        attributes,
        type: oidcSession.userType,
        id: oidcSession.profile.userId,
      };

      const config = {
        url: `users/${oidcSession.profile.userId}`,
        method: 'patch',
        data: {
          data: dataProfile,
        },
      };
      /** @type any */
      const [request] = makeApiRequest(config, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'physician', type: 'userUpdate', error });
        throw error;
      }

      if (attributes.firstName && attributes.lastName) {
        const normalizedUserProfile = normalizeUserProfile(dataProfile);
        this.onUpdated({ ...normalizedUserProfile.attributes });
      } else {
        this.onUpdated({ ...attributes });
      }
    },
    async updatePracticeAddress(data, { oidcSession, superuser }) {
      const attributes = Object.entries(data).reduce(
        (memo, [key, value]) => ({
          ...memo,
          [key]: value || null,
        }),
        {},
      );

      const dataProfile = {
        attributes,
        type: UserTypes.PHYSICIAN,
        id: superuser.attributes.physicianUserId || superuser.id,
      };

      const config = {
        url: `users/${superuser.attributes.physicianUserId || superuser.id}`,
        method: 'patch',
        data: {
          data: dataProfile,
        },
      };
      /** @type any */
      const [request] = makeApiRequest(config, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'physician', type: 'userUpdate', error });
        throw error;
      }

      this.onUpdated({ ...attributes });
    },
    async editContactPage(
      {
        metaRobotsContent,
        personalNote,
        widgetSettings,
        patientRegistrationRequired,
        openingHours,
        services,
        inquiryManagementSettings,
      },
      { oidcSession },
    ) {
      dispatch.errors.remove({ realm: 'contactPage', type: 'update' });
      const config = {
        url: 'physician-pages',
        method: 'patch',
        data: {
          data: {
            type: 'physician-pages-settings',
            attributes: {
              metaRobotsContent,
              personalNote,
              widgetSettings,
              inquiryManagementSettings,
              patientRegistrationRequired,
              openingHours,
              services,
            },
          },
        },
      };
      const [request] = makeApiRequest(config, oidcSession);
      const {
        error,
        data: { data },
      } = await request;

      if (error) {
        dispatch.errors.add({ realm: 'contactPage', type: 'update', error });
        throw error;
      } else {
        this.onUpdated({
          pages: {
            metaRobotsContent: data.pages.metaRobotsContent,
            personalNote: data.pages.personalNote,
            widgetSettings: data.pages.widgetSettings,
            inquiryManagementSettings: data.pages.inquiryManagementSettings,
            patientRegistrationRequired: data.pages.patientRegistrationRequired,
            openingHours: data.pages.openingHours,
            services: data.pages.services,
          },
        });
      }
    },
    async updateChatSettings(chatSettings, { oidcSession }) {
      const config = {
        url: `physicians/${oidcSession.profile.userId}/chat-settings`,
        method: 'patch',
        data: {
          data: {
            type: 'chat-settings',
            attributes: chatSettings,
          },
        },
      };

      const [request] = makeApiRequest(config, oidcSession);
      const { error, data } = await request;

      if (error) {
        dispatch.errors.add({
          realm: 'chatSettings',
          type: 'updateChatSettings',
          error,
        });
        throw error;
      }

      this.onUpdated({ chatSettings: data.data.chatSettings });
    },
    async updatePushSettings(push, { oidcSession }) {
      const config = {
        url: `users/${oidcSession.profile.userId}/notification-settings`,
        method: 'patch',
        data: {
          data: {
            type: 'notification-settings',
            attributes: { push },
          },
        },
      };

      const [request] = makeApiRequest(config, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({
          realm: 'pushSettings',
          type: 'userUpdate',
          error,
        });
        throw error;
      }

      const { notificationSettings } = data.data;
      this.onUpdated({ notificationSettings });
    },
    async updateSmsSettings(sms, { oidcSession, superuser }) {
      const config = {
        url: `/users/${superuser.attributes.physicianUserId || superuser.id}/notification-settings`,
        method: 'patch',
        data: {
          data: {
            type: 'notification-settings',
            attributes: { sms },
          },
        },
      };

      const [request] = makeApiRequest(config, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({
          realm: 'smsSettings',
          type: 'updateSmsSettings',
          error,
        });
        throw error;
      }
      const { notificationSettings } = data.data;
      this.onUpdated({ notificationSettings });
    },
    async updatePatientProfile(profile, { oidcSession }) {
      const { firstName, lastName, dateOfBirth, gender, phoneNumbers } = profile;

      const config = {
        url: `patients/${oidcSession.profile.userId}`,
        method: 'patch',
        data: {
          data: {
            type: 'patients',
            attributes: {
              firstName,
              lastName,
              dateOfBirth,
              gender,
              phoneNumbers,
            },
          },
        },
      };

      const [request] = makeApiRequest(config, oidcSession);
      const { error, data } = await request;

      if (error) {
        dispatch.errors.add({
          realm: 'patientProfile',
          type: 'patientUpdate',
          error,
        });
        throw error;
      }

      this.onUpdated({
        firstName: data.data.attributes.firstName,
        lastName: data.data.attributes.lastName,
        dateOfBirth: data.data.attributes.dateOfBirth,
        gender: data.data.attributes.gender,
        phoneNumbers: data.data.attributes.phoneNumbers,
        fullNameWithTitle: getFullNameWithTitle.get(data.data.attributes),
      });
    },
    async createTag(payload, { oidcSession, superuser }) {
      const url = `physicians/${superuser.attributes.physicianUserId || superuser.id}/relationships/tags`;
      const config = {
        url,
        method: 'post',
        data: {
          data: {
            type: 'tags',
            attributes: payload,
          },
        },
      };
      const [request] = makeApiRequest(config, oidcSession);

      const { error, data } = await request;

      if (error) {
        throw error;
      }

      this.onTagCreated(data.data);
    },
    async updateTag({ id, key, ...payload }, { oidcSession, superuser }) {
      const url = `physicians/${superuser.attributes.physicianUserId || superuser.id}/relationships/tags/${id}`;
      const config = {
        url,
        method: 'patch',
        data: {
          data: {
            id,
            type: 'tags',
            attributes: payload,
          },
        },
      };
      const [request] = makeApiRequest(config, oidcSession);

      const { error, data } = await request;

      if (error) {
        throw error;
      }

      this.onTagUpdated(data.data);
    },
    async deleteTag(id, { oidcSession, superuser }) {
      const url = `physicians/${superuser.attributes.physicianUserId || superuser.id}/relationships/tags/${id}`;
      const config = {
        url,
        method: 'delete',
      };
      const [request] = makeApiRequest(config, oidcSession);
      const { error } = await request;

      if (error) {
        throw error;
      }

      this.onTagDeleted(id);
    },
    async identifyHubspotUser(_, { oidcSession, superuser }) {
      const config = {
        url: `users/${superuser.id}/external-services/hubspot/identification`,
        method: 'post',
      };
      const [request] = makeApiRequest(config, oidcSession);
      const { error, data } = await request;

      if (error) {
        dispatch.errors.add({
          realm: 'hubspot',
          type: 'identifyUser',
          error,
        });
        throw error;
      }

      return data.data.identificationToken;
    },
  }),
};

export default userProfile;
